// HTTP endpoints and what each one reaches. Minimal API endpoints are found while walking bodies
// (walker.ts); controller actions are found here from the declarations. `endpointReach` follows an
// endpoint's handler through the calls of the code map (reach.ts).
import type { CsAttribute, Expr } from '../csharp/ast';
import type { CodeEndpoint, CodeMap } from './model';
import { reach, reachIndex, type Reach, type ReachOptions } from './reach';
import type { MemberSymbol, SymbolTable, Ty, TypeSymbol } from './symbols';
import { joinRoute } from './walker';

const HTTP_METHODS: Record<string, string> = {
  HttpGet: 'GET',
  HttpPost: 'POST',
  HttpPut: 'PUT',
  HttpDelete: 'DELETE',
  HttpPatch: 'PATCH',
  HttpHead: 'HEAD',
  HttpOptions: 'OPTIONS',
};

/** `ApiController` for `[ApiController]` and `[Microsoft.AspNetCore.Mvc.ApiControllerAttribute]`. */
export function attributeName(a: CsAttribute): string {
  const name = a.name.slice(a.name.lastIndexOf('.') + 1);
  return name.endsWith('Attribute') ? name.slice(0, -'Attribute'.length) : name;
}

function stringOf(e: Expr | undefined): string | null {
  return e?.kind === 'literal' && e.literal === 'string' ? String(e.value) : null;
}

/** The route template an attribute gives: its first argument, or `Template =`. */
function templateOf(a: CsAttribute): string | null {
  const named = a.args.find((arg) => arg.name === 'Template' || arg.name === 'template');
  return stringOf((named ?? a.args.find((arg) => arg.name === null))?.value);
}

function attributesOf(sym: TypeSymbol): CsAttribute[] {
  return sym.parts.flatMap((p) => p.cls.attributes);
}

/** The type and its base classes in the code, most derived first. */
export function chain(table: SymbolTable, sym: TypeSymbol): TypeSymbol[] {
  const out: TypeSymbol[] = [];
  for (let t: TypeSymbol | null = sym; t && !out.includes(t); ) {
    out.push(t);
    const base: Ty | null = table.basesOf(t).base;
    t = base?.k === 'code' ? base.sym : null;
  }
  return out;
}

function isController(table: SymbolTable, sym: TypeSymbol): boolean {
  if (sym.kind !== 'class' || sym.parts.some((p) => p.cls.modifiers.includes('abstract') || p.cls.modifiers.includes('static'))) return false;
  const names = chain(table, sym).flatMap((t) => attributesOf(t).map(attributeName));
  if (attributesOf(sym).some((a) => attributeName(a) === 'NonController')) return false;
  if (names.includes('ApiController') || names.includes('Controller')) return true;
  const frameworkBase = table.externalBases({ k: 'code', sym, args: [] }).some((b) => b.k === 'ext' && (b.name === 'ControllerBase' || b.name === 'Controller'));
  return frameworkBase || (sym.name.endsWith('Controller') && table.hasExternalBase({ k: 'code', sym, args: [] }));
}

/** `[controller]`, `[action]` and `[area]` in a template, as ASP.NET Core fills them in. */
function fillTokens(template: string, controller: string, action: string, area: string | null): string {
  return template
    .replace(/\[controller\]/gi, controller)
    .replace(/\[action\]/gi, action)
    .replace(/\[area\]/gi, area ?? '');
}

/** The endpoints of the controllers of the code: one per action and HTTP method attribute. */
export function controllerEndpoints(table: SymbolTable, taken: Set<string>): CodeEndpoint[] {
  const out: CodeEndpoint[] = [];
  for (const sym of table.typeOrder) {
    if (!isController(table, sym)) continue;
    const types = chain(table, sym);
    const classRoute = types.flatMap((t) => attributesOf(t).filter((a) => attributeName(a) === 'Route')).map(templateOf).find((t) => t !== null) ?? null;
    const area = types.flatMap((t) => attributesOf(t).filter((a) => attributeName(a) === 'Area')).map(templateOf).find((t) => t !== null) ?? null;
    const controller = sym.name.endsWith('Controller') ? sym.name.slice(0, -'Controller'.length) : sym.name;
    for (const m of sym.members) {
      if (!isAction(m)) continue;
      const attributes = m.decl.kind === 'method' ? m.decl.method.attributes : [];
      // ASP.NET Core drops "Async" from action names.
      const action = m.name.endsWith('Async') ? m.name.slice(0, -'Async'.length) : m.name;
      const methodRoute = attributes.filter((a) => attributeName(a) === 'Route').map(templateOf).find((t) => t !== null) ?? null;
      const routes: { methods: string[]; template: string | null }[] = [];
      for (const a of attributes) {
        const name = attributeName(a);
        if (HTTP_METHODS[name]) routes.push({ methods: [HTTP_METHODS[name]], template: templateOf(a) ?? methodRoute });
        else if (name === 'AcceptVerbs') routes.push({ methods: a.args.filter((x) => x.name === null).flatMap((x) => (stringOf(x.value) ? [stringOf(x.value)!.toUpperCase()] : [])), template: a.args.find((x) => x.name === 'Route') ? stringOf(a.args.find((x) => x.name === 'Route')!.value) : methodRoute });
      }
      if (!routes.length) routes.push({ methods: [], template: methodRoute });
      for (const r of routes) {
        let route: string;
        if (r.template !== null && (r.template.startsWith('/') || r.template.startsWith('~/'))) route = joinRoute('', r.template.replace(/^~/, ''));
        else if (classRoute === null && r.template === null) route = joinRoute(area ?? '', `${controller}/${action}`);
        else route = joinRoute(classRoute ?? '', r.template ?? '');
        route = fillTokens(route, controller, action, area);
        const base = `${r.methods.join(',') || 'ANY'} ${route}`;
        let id = base;
        for (let n = 2; taken.has(id); n++) id = `${base}#${n}`;
        taken.add(id);
        out.push({ id, methods: r.methods, route, kind: 'controller', handler: m.id, file: m.ctx.path, line: m.line });
      }
    }
  }
  return out;
}

function isAction(m: MemberSymbol): boolean {
  if (m.kind !== 'method' || m.isStatic || !m.hasBody || !m.modifiers.includes('public')) return false;
  const attributes = m.decl.kind === 'method' ? m.decl.method.attributes : [];
  return !attributes.some((a) => attributeName(a) === 'NonAction');
}

// ------------------------------------------------------------------ reach

export interface EndpointReach extends Reach {
  endpoint: CodeEndpoint;
}

/**
 * Everything an endpoint's handler reaches in the code, step by step, and on to the handlers of
 * what it hands to a mediator. Test code is left out: an implementation in a test file (a fake
 * repository) is not what the endpoint runs.
 */
export function endpointReach(map: CodeMap, endpointId: string, options: ReachOptions = {}): EndpointReach | null {
  const endpoint = map.endpoints.find((e) => e.id === endpointId);
  if (!endpoint) return null;
  const { testFiles } = reachIndex(map);
  const notTests = (m: { file: string }) => !testFiles.has(m.file);
  const found = reach(map, { members: endpoint.handler ? [endpoint.handler] : [], endpoint: endpoint.sameHandler ?? endpoint.id, file: endpoint.file, dispatchTo: (_, impl) => notTests(impl), handledBy: notTests }, options);
  return { endpoint, ...found };
}
